// Shopping subsection — list selector + add bar + items grouped by
// category. Plan-with-AI and Share are stubbed for Phase 1.5.

import { useEffect, useMemo, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { useShopping } from "../../lib/use-shopping";
import { confirm } from "../../components/confirm";
import { api, type ShoppingItem } from "../../lib/rpc";
import { QueuedDot } from "../../components/queued-dot";
import type { NotifyFn } from "../../components/toast";
import { sse } from "../../lib/sse";
import { ListSelector } from "./list-selector";
import { ShareListModal } from "./share-list-modal";
import { PlanWithAI } from "./plan-with-ai";

const MISC = "Misc";

// Split a free-form add string into (quantity, name) when it leads with
// a numeric quantity. Matches:
//   "2 onions"           → qty="2",          name="onions"
//   "1 lb chicken"       → qty="1 lb",       name="chicken"
//   "1.5 lbs ground beef"→ qty="1.5 lbs",    name="ground beef"
//   "2 cans tomato soup" → qty="2 cans",     name="tomato soup"
//   "1 dozen eggs"       → qty="1 dozen",    name="eggs"
//   "olive oil"          → qty=null,         name="olive oil"
// The unit set is intentionally narrow — common kitchen units only — so
// "100 bread" doesn't unexpectedly become qty="100 bread" name="". When
// no unit follows the number, we accept a bare digit as the quantity.
const QTY_UNIT = /(?:lbs?|oz|cups?|tsps?|tbsps?|kg|g|ml|l|cans?|jars?|bottles?|bags?|boxes?|cartons?|dozen|pkg|packets?|sticks?|cloves?|heads?|loaves?|loaf|bunch|bunches|ears?|sprigs?|pints?|quarts?|gal|gallons?)/i;
const QTY_PARSE = new RegExp(`^(\\d+(?:\\.\\d+)?)(\\s+(${QTY_UNIT.source}))?\\s+(.+)$`, "i");
function splitQty(input: string): { quantity: string | null; text: string } {
  const m = QTY_PARSE.exec(input.trim());
  if (!m) return { quantity: null, text: input.trim() };
  const num = m[1];
  const unit = (m[3] || "").trim();
  const name = m[4].trim();
  if (!name) return { quantity: null, text: input.trim() };
  return { quantity: unit ? `${num} ${unit}` : num, text: name };
}

interface ShoppingProps {
  notify: NotifyFn;
}

export function Shopping({ notify }: ShoppingProps) {
  const s = useShopping();
  const [draft, setDraft] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set(["Got it"]));
  // Show the most recent 10 purchased items by default — every check
  // accumulates here, so on a long-running list (groceries-of-doom) we
  // were rendering 50+ rows under "Got it" forcing a scroll fest.
  // Keep them all in the DB; just hide older rows behind a toggle.
  const [showAllDone, setShowAllDone] = useState(false);
  const DONE_PREVIEW = 10;
  const [shareOpen, setShareOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);

  // Shopping → pantry bridge undo toast. The server auto-adds a
  // pantry item on every check-off; we listen for the corresponding
  // pantry:created SSE event (tagged with fromCheckoff so we can
  // distinguish it from manual pantry adds) and surface an
  // actionable toast that lets the user nuke the auto-add. Toast is
  // suppressed during shopping clearPurchased bulk operations to
  // avoid a wall of toasts.
  useEffect(() => {
    return sse.on((ev: any) => {
      if (ev?.type !== "pantry:created") return;
      if (!ev.fromCheckoff || !ev.item) return;
      const item = ev.item;
      const expHint = item.expires ? ` · expires ${item.expires}` : "";
      notify(
        `${item.name} → pantry${expHint}`,
        {
          action: {
            label: "Undo",
            onClick: async () => {
              try { await api.pantryDelete(item.id); }
              catch (e: any) { notify(e?.message || "couldn't undo"); }
            },
          },
        },
      );
    });
  }, [notify]);

  const open = s.items.filter((i) => !i.purchased);
  // Sort `done` by purchasedAt DESC (most recently checked off first).
  // Pre-migration rows that never got re-toggled have purchasedAt=null
  // — those sink to the bottom and tiebreak by position ASC so they
  // read naturally to anyone scrolling past the recent items.
  const done = s.items.filter((i) => i.purchased).slice().sort((a, b) => {
    const aT = a.purchasedAt || "";
    const bT = b.purchasedAt || "";
    if (aT && bT) return aT < bT ? 1 : aT > bT ? -1 : 0;
    if (aT) return -1;
    if (bT) return 1;
    return (a.position || 0) - (b.position || 0);
  });

  const grouped = useMemo<Record<string, ShoppingItem[]>>(() => {
    const m: Record<string, ShoppingItem[]> = {};
    for (const i of open) {
      const cat = i.category || MISC;
      (m[cat] ||= []).push(i);
    }
    return m;
  }, [open]);

  const groupKeys = Object.keys(grouped).sort();

  const toggleCollapse = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onAdd = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setDraft("");
    // Parse leading qty from the input — "2 onions", "1 lb chicken",
    // etc. Honors the placeholder's promise that wasn't previously
    // implemented (server stored "2 onions" as text and qty=null).
    // qty flows through to the pantry bridge on check-off so the
    // resulting pantry row has a real quantity instead of the "1"
    // default fallback.
    const { quantity, text } = splitQty(trimmed);
    await s.add(text, quantity || undefined);
  };

  return (
    <div className="shop-main">
      <div className="shop-toolbar">
        <ListSelector
          lists={s.lists}
          activeListId={s.activeListId}
          setActiveListId={s.setActiveListId}
          openCount={open.length}
          totalCount={s.items.length}
          onCreateList={s.createList}
          onDeleteList={(id) => { s.deleteList(id); notify("List deleted"); }}
        />
        <button
          className="btn btn-primary plan-btn"
          onClick={() => {
            if (!s.activeList) { notify("Make a list first"); return; }
            setPlanOpen(true);
          }}
          title="Plan with AI"
          type="button"
        >
          <Icon name="sparkle" size={14} />
          <span className="plan-btn-label">Plan with AI</span>
        </button>
        <button
          className="btn share-btn"
          onClick={() => {
            if (!s.activeList) { notify("Make a list first"); return; }
            setShareOpen(true);
          }}
          type="button"
          title="Share this list with specific people"
        >
          <Icon name="people" size={14} />
          <span>Share</span>
        </button>
      </div>

      <div className="shop-add">
        <Icon name="plus" size={16} />
        <input
          placeholder={`Add to ${s.activeList?.name || "list"}…  (try "2 onions" or "the good olive oil")`}
          value={draft}
          onInput={(e) => setDraft((e.currentTarget as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === "Enter") onAdd(); }}
          disabled={s.loading || !s.activeListId}
        />
        <span className="kbd">⏎</span>
      </div>

      {s.loading && (
        <p style={{ color: "var(--text-dim)", fontFamily: "var(--f-mono)", fontSize: 12, padding: "var(--pad-4) 0" }}>
          loading…
        </p>
      )}

      {!s.loading && (
        <div className="shop-list-wrap">
          {groupKeys.map((cat) => (
            <ShopGroup
              key={cat}
              label={cat}
              count={grouped[cat].length}
              collapsed={collapsed.has(cat)}
              onToggle={() => toggleCollapse(cat)}
            >
              {grouped[cat].map((item) => (
                <ShopItemRow
                  key={item.id}
                  item={item}
                  onToggle={() => s.toggle(item.id)}
                  onRemove={() => s.remove(item.id)}
                  onChangeQty={(q) => s.update(item.id, { quantity: q })}
                />
              ))}
            </ShopGroup>
          ))}

          {done.length > 0 && (() => {
            // `done` is already sorted purchasedAt DESC (then by
            // position ASC for legacy null timestamps). Just slice
            // the head — top N most recently checked off.
            const visible = showAllDone ? done : done.slice(0, DONE_PREVIEW);
            const hidden = done.length - visible.length;
            return (
              <ShopGroup
                label="Got it"
                count={done.length}
                collapsed={collapsed.has("Got it")}
                onToggle={() => toggleCollapse("Got it")}
                onClear={async () => {
                  const ok = await confirm({
                    title: "Clear purchased?",
                    message: `${done.length} purchased item${done.length === 1 ? "" : "s"} will be removed.`,
                    confirmLabel: "Clear",
                    danger: true,
                  });
                  if (!ok) return;
                  await s.clearPurchased();
                  notify("Cleared");
                }}
              >
                {hidden > 0 && (
                  <button
                    className="shop-done-more"
                    type="button"
                    onClick={() => setShowAllDone(true)}
                  >
                    Show {hidden} older item{hidden === 1 ? "" : "s"}
                  </button>
                )}
                {visible.map((item) => (
                  <ShopItemRow
                    key={item.id}
                    item={item}
                    onToggle={() => s.toggle(item.id)}
                    onRemove={() => s.remove(item.id)}
                    onChangeQty={(q) => s.update(item.id, { quantity: q })}
                  />
                ))}
                {showAllDone && done.length > DONE_PREVIEW && (
                  <button
                    className="shop-done-more"
                    type="button"
                    onClick={() => setShowAllDone(false)}
                  >
                    Show recent only
                  </button>
                )}
              </ShopGroup>
            );
          })()}

          {s.items.length === 0 && (
            <div className="shop-empty">
              <Icon name="food" size={36} />
              <div className="shop-empty-line">Nothing on the list. Lucky you.</div>
            </div>
          )}
        </div>
      )}

      <ShareListModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        list={s.activeList}
        notify={notify}
      />

      <PlanWithAI
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        list={s.activeList}
        notify={notify}
        onApplied={s.refresh}
      />
    </div>
  );
}

function ShopGroup({
  label, count, collapsed, onToggle, onClear, children,
}: {
  label: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  onClear?: () => void;
  children: any;
}) {
  return (
    <div className="shop-grp" data-collapsed={collapsed}>
      <button className="shop-grp-hd" onClick={onToggle} type="button">
        <span className="shop-grp-chev" aria-hidden>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
        <span className="shop-grp-label">{label}</span>
        <span className="shop-grp-count">{count}</span>
        {onClear && (
          <span
            style={{ marginLeft: 6, color: "var(--text-dim)", fontFamily: "var(--f-mono)", fontSize: 10.5, padding: "1px 8px", borderRadius: 6 }}
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            title="Clear purchased items"
          >
            clear
          </span>
        )}
      </button>
      <div className="shop-grp-body">
        <div className="shop-items">{children}</div>
      </div>
    </div>
  );
}

function ShopItemRow({
  item, onToggle, onRemove, onChangeQty,
}: {
  item: ShoppingItem;
  onToggle: () => void;
  onRemove: () => void;
  // Empty string means "clear" — the server's shopping.updateItem
  // handler collapses an empty/whitespace-only quantity to NULL in the
  // DB via `String(qty).trim().slice(0,40) || null`. Using `string`
  // here (rather than `string | null`) keeps the call-site type
  // compatible with Partial<ShoppingItem> under exactOptionalPropertyTypes.
  onChangeQty: (q: string) => void;
}) {
  const [editingQty, setEditingQty] = useState(false);
  const [qtyDraft, setQtyDraft] = useState(item.quantity || "");

  const startEdit = (e: Event) => {
    e.stopPropagation();
    setQtyDraft(item.quantity || "");
    setEditingQty(true);
  };

  const saveEdit = () => {
    const trimmed = qtyDraft.trim();
    setEditingQty(false);
    // No-op if nothing changed (e.g. user opened + blurred). Avoids a
    // useless RPC + SSE roundtrip that would re-render every connected
    // client for this list. Treat current null/undefined as "" for the
    // comparison so going-from-empty-to-empty is also a no-op.
    if ((item.quantity || "") === trimmed) return;
    onChangeQty(trimmed);
  };

  const cancelEdit = () => {
    setQtyDraft(item.quantity || "");
    setEditingQty(false);
  };

  return (
    <div className="shop-item" data-checked={item.purchased}>
      <span
        className="shop-cbx"
        data-checked={item.purchased}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        role="checkbox"
        aria-checked={item.purchased}
      >
        <Icon name="check" size={12} />
      </span>
      {item.emoji && <span className="shop-item-emoji">{item.emoji}</span>}
      <span className="shop-item-name">{item.text}</span>
      <QueuedDot queued={item._queued} />
      {editingQty ? (
        <input
          className="shop-item-qty-edit"
          // Compact inline editor — replaces the qty chip while open.
          // Enter saves, Esc cancels, blur saves (so tapping elsewhere
          // commits without an explicit confirm). Restricted to ~10
          // chars matching the server's 40-char cap with room for unit
          // text like "1.5 lbs".
          value={qtyDraft}
          maxLength={40}
          autoFocus
          placeholder="qty"
          onInput={(e) => setQtyDraft((e.currentTarget as HTMLInputElement).value)}
          onBlur={saveEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); (e.currentTarget as HTMLInputElement).blur(); }
            else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
            // Eat clicks/keys so we don't bubble to the row toggle.
            e.stopPropagation();
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : item.quantity ? (
        // Existing qty — tap to edit.
        <button
          type="button"
          className="shop-item-qty"
          onClick={startEdit}
          title="Edit quantity"
        >
          {item.quantity}
        </button>
      ) : (
        // No qty yet — render a faint affordance so a "1 lb chicken"
        // typed without the qty syntax can still get a quantity added
        // after the fact (matters most when the item flows to pantry
        // on check-off — pantry qty falls back to "1" without this).
        <button
          type="button"
          className="shop-item-qty-add"
          onClick={startEdit}
          title="Add quantity"
        >
          + qty
        </button>
      )}
      <button className="shop-item-trash" onClick={onRemove} title="Delete" type="button">
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}
